/**
 * Frontend mirror of functions-forms/src/kb-assistant/coverageScan.ts.
 * Keep both in sync — Cloud Functions cannot import from src/.
 */

/**
 * Deterministic KB coverage scan.
 * Pure functions only: no Gemini, no Firestore, no network.
 *
 * Highest-value check: a /pricing, /plans, or /rates URL discovered on the
 * site but missing from the scraped page list — the Aug 23 GAM gap.
 */

export const COVERAGE_TOPICS = [
  'pricing',
  'services',
  'serviceArea',
  'hours',
  'contactRoutes',
  'disqualifiers',
  'differentiators',
] ;

 



































const HIGH_VALUE_SEGMENTS = ['pricing', 'plans', 'rates'];

const MAX_EVIDENCE = 5;
const SNIPPET_RADIUS = 60;

const TOPIC_PATTERNS = {
  pricing: [
    /(?:[$€£]\s?\d[\d,]*(?:\.\d{1,2})?|\b(?:usd|eur|gbp)\s?\d[\d,]*)/gi,
    /\b(?:pricing|price list|our (?:prices|rates)|retainer|starting at|per month|\/mo\b|monthly (?:fee|retainer|minimum)|hourly rate)\b/gi,
  ],
  services: [
    /\b(?:our services|complete (?:marketing )?services|we (?:offer|provide|specialize(?:\s+in)?|handle|build)|services we|what we do|products? we)\b/gi,
    /\b(?:search engine optimization|\bseo\b|google ads|web design|ppc management)\b/gi,
  ],
  serviceArea: [
    /\b(?:service area|areas? we serve|we serve|based in|located in|nationwide|across the (?:country|united states|u\.?s\.?)|serving clients)\b/gi,
    /\b(?:south florida|united states|across the united states)\b/gi,
  ],
  hours: [
    /\b(?:hours of operation|business hours|open (?:from|until)|monday[\s–—-]+friday|mon(?:day)?[\s–—-]+fri|24\/7|we (?:reply|respond) within|response (?:time|expectations?))\b/gi,
    /\b\d{1,2}:\d{2}\s*(?:am|pm)\b/gi,
  ],
  contactRoutes: [
    /\b(?:contact us|email us|call us|book (?:a |an )?(?:call|appointment|audit)|schedule (?:a |an )?(?:call|appointment)|phone|tel\s*:)\b/gi,
    /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
    /\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}/g,
  ],
  disqualifiers: [
    /\bwe (?:do not|don't|won'?t|cannot|can't) (?:offer|serve|work|cover|handle|install|treat|take|accept|book|do)\b/gi,
    /\b(?:we are not|we're not) (?:able|available|the right)\b/gi,
    /\b(?:outside (?:our|of (?:our )?)(?:service|coverage|area)|do not (?:serve|cover|offer))\b/gi,
  ],
  differentiators: [
    /\b(?:unlike|proprietary|certified|award(?:-winning)?|testimonial|what makes us|google partner|since \d{4}|serving clients since|our own|case study)\b/gi,
  ],
};

const ABSENCE_REASONS = {
  pricing: 'No pricing signals (currency figures or pricing language) found in the knowledge base.',
  services: 'No services or products language found in the knowledge base.',
  serviceArea: 'No service area or location language found in the knowledge base.',
  hours: 'No hours or response-time language found in the knowledge base.',
  contactRoutes: 'No contact routes (email, phone, booking, or contact CTA) found in the knowledge base.',
  disqualifiers: 'No disqualifiers (what they do not do) found in the knowledge base.',
  differentiators: 'No differentiators or proof language found in the knowledge base.',
};







function findMatches(text, re) {
  const flags = re.flags.includes('g') ? re.flags : `${re.flags}g`;
  const global = new RegExp(re.source, flags);
  const out = [];
  let m;
  while ((m = global.exec(text)) !== null) {
    out.push({ match: m[0], index: m.index });
    if (m[0].length === 0) global.lastIndex += 1;
  }
  return out;
}

function snippetAround(text, index, length) {
  const start = Math.max(0, index - SNIPPET_RADIUS);
  const end = Math.min(text.length, index + length + SNIPPET_RADIUS);
  let snippet = text.slice(start, end).replace(/\s+/g, ' ').trim();
  if (start > 0) snippet = `...${snippet}`;
  if (end < text.length) snippet = `${snippet}...`;
  return snippet;
}

function collectEvidence(text, patterns) {
  const seen = new Set();
  const evidence = [];
  for (const pattern of patterns) {
    for (const hit of findMatches(text, pattern)) {
      const snippet = snippetAround(text, hit.index, hit.match.length);
      if (!snippet || seen.has(snippet)) continue;
      seen.add(snippet);
      evidence.push({ snippet });
      if (evidence.length >= MAX_EVIDENCE) return evidence;
    }
  }
  return evidence;
}

function scanTopic(topic, kbContent) {
  const evidence = collectEvidence(kbContent, TOPIC_PATTERNS[topic]);
  if (evidence.length > 0) {
    return { topic, status: 'present', evidence };
  }
  return { topic, status: 'missing', evidence: [{ snippet: ABSENCE_REASONS[topic] }] };
}

function splitUrl(raw) {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    const isRelative = trimmed.startsWith('/') && !trimmed.startsWith('//');
    const withProto = /^[a-z][a-z0-9+.-]*:/i.test(trimmed)
      ? trimmed
      : trimmed.startsWith('//')
        ? `https:${trimmed}`
        : isRelative
          ? `https://placeholder.invalid${trimmed}`
          : `https://${trimmed}`;
    const parsed = new URL(withProto);
    const host = parsed.hostname.replace(/^www\./i, '').toLowerCase();
    const path = (parsed.pathname.replace(/\/+$/, '') || '/').toLowerCase();
    return { host: isRelative ? '' : host, path, original: trimmed };
  } catch (e) {
    return null;
  }
}

function highValueKind(path) {
  const segments = path.split('/').filter(Boolean);
  for (const kind of HIGH_VALUE_SEGMENTS) {
    if (segments.includes(kind)) return kind;
  }
  return null;
}

function isCoveredByScraped(discovered, scraped) {
  return scraped.some((page) => {
    if (discovered.path !== page.path) return false;
    if (!discovered.host || !page.host) return true;
    return discovered.host === page.host;
  });
}

const KIND_ORDER = { pricing: 0, plans: 1, rates: 2 };

/**
 * URLs whose path contains /pricing, /plans, or /rates that appear in
 * discovered site links but are not among the scraped pages.
 */
export function findMissingHighValuePages(
  scrapedPageUrls,
  discoveredLinks
) {
  const scraped = scrapedPageUrls.map(splitUrl).filter((u) => u != null);
  const seen = new Set();
  const missing = [];

  for (const raw of discoveredLinks) {
    const parts = splitUrl(raw);
    if (!parts) continue;
    const kind = highValueKind(parts.path);
    if (!kind) continue;
    if (isCoveredByScraped(parts, scraped)) continue;

    const dedupeKey = `${kind}:${parts.host}${parts.path}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);

    missing.push({
      url: parts.original,
      kind,
      reason: `Found ${parts.original} on the site, but it is not among the scraped pages.`,
    });
  }

  missing.sort((a, b) => {
    const kindDiff = KIND_ORDER[a.kind] - KIND_ORDER[b.kind];
    if (kindDiff !== 0) return kindDiff;
    return a.url.localeCompare(b.url);
  });
  return missing;
}

/**
 * Scan KB text plus scraped/discovered URLs for topic coverage.
 * Deterministic: same input always yields the same result.
 */
export function scanKbCoverage(input) {
  const kbContent = input.kbContent || '';
  const scrapedPageUrls = input.scrapedPageUrls || [];
  const discoveredLinks = input.discoveredLinks || [];

  const coverage = {} ;
  for (const topic of COVERAGE_TOPICS) {
    coverage[topic] = scanTopic(topic, kbContent);
  }

  return {
    coverage,
    missingHighValuePages: findMissingHighValuePages(scrapedPageUrls, discoveredLinks),
  };
}
